import { useState } from "react";
import { generatePath, useNavigate } from "react-router";
import { keepPreviousData } from "@tanstack/react-query";
import {
  type ColDef,
  type RowClickedEvent,
  type SortChangedEvent,
} from "ag-grid-community";
import { ListIllustration } from "vega-illustrations";

import { usePagination } from "@vega-api/custom/utils/usePagination";
import { useGetInsightsLimitedPartners } from "@vega-api/generated/insights/insights";
import { type LimitedPartnerInsight } from "@vega-api/generated/types/limitedPartnerInsight";
import { DataGrid } from "@vega-core/components/DataGrid/DataGrid";
import { SearchInput } from "@vega-core/components/Form/Controls/Input/SearchInput/SearchInput";
import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
  usePaginationState,
} from "@vega-core/hooks/usePaginationState";
import { m } from "@vega-core/paraglide/messages";
import { useDebounce } from "@vega-core/utils/useDebounce";
import { CoInvestmentFullPaths } from "@vega-modules/paths";

import {
  DEFAULT_SEARCH_STALE_TIME,
  SEARCH_DEBOUNCE_DELAY_MS,
} from "../../constants/searchConstants";
import { useOrganisationsTableColumns } from "../../hooks/useOrganisationsTableColumns";
import { ReportSectionMessage } from "../../pages/components/ReportSectionMessage";

const NO_SELECTION_OPTIONS = {
  checkboxes: false,
  headerCheckbox: false,
  enableClickSelection: false,
} as const;

// Sorting is server-driven; the no-op comparator stops ag-grid re-ordering the
// already-sorted page client-side (money columns mix currencies, so a client
// sort by raw amount would diverge from the backend order).
const DEFAULT_COL_DEF: ColDef<LimitedPartnerInsight> = {
  sortable: true,
  resizable: true,
  suppressMovable: true,
  comparator: () => 0,
};

export const OrganisationsTable = () => {
  const navigate = useNavigate();
  const { columnDefs, renderColumnToggle } = useOrganisationsTableColumns();

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, SEARCH_DEBOUNCE_DELAY_MS);
  const [sort, setSort] = useState<string[] | undefined>(undefined);
  const { currentPage, setCurrentPage, currentPageSize, setCurrentPageSize } =
    usePaginationState(DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE);

  const {
    data: response,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useGetInsightsLimitedPartners(
    {
      page: currentPage,
      pageSize: currentPageSize,
      searchQuery: debouncedSearch || undefined,
      sort,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        staleTime: DEFAULT_SEARCH_STALE_TIME,
      },
    },
  );

  const limitedPartners = response?.data ?? [];

  const handleRowClicked = (event: RowClickedEvent<LimitedPartnerInsight>) => {
    if (!event.data) return;

    navigate(
      generatePath(CoInvestmentFullPaths.CoInvestmentInsightsLimitedPartner, {
        limitedPartnerId: event.data.limitedPartnerId,
      }),
    );
  };

  const handleSortChanged = (
    event: SortChangedEvent<LimitedPartnerInsight>,
  ) => {
    const sortedColumn = event.api
      .getColumnState()
      .find((column) => column.sort);
    const sortField = sortedColumn?.colId;

    setCurrentPage(DEFAULT_PAGE_NUMBER);

    if (!sortField) {
      setSort(undefined);

      return;
    }

    const prefix = sortedColumn.sort === "desc" ? "-" : "";
    setSort([`${prefix}${sortField}`]);
  };

  const { paginationElement } = usePagination({
    meta: response?.meta ?? null,
    isLoading: isFetching,
    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
    filterDependencies: [debouncedSearch, sort?.join(",")],
    currentPage,
    setCurrentPage,
    currentPageSize,
    setCurrentPageSize,
  });

  const hasNoOrganisations =
    !isFetching &&
    !searchQuery &&
    !debouncedSearch &&
    (response?.meta.count ?? 0) === 0;
  const showToolbar = !hasNoOrganisations;

  return (
    <div
      className="flex flex-col gap-4"
      data-testid="insights-organisations-table"
    >
      {isError ? (
        <ReportSectionMessage
          dataTestId="insights-organisations-error"
          message={m.common_error_somethingWentWrong()}
          onRetry={refetch}
        />
      ) : (
        <>
          {showToolbar ? (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <SearchInput
                className="min-w-72"
                name="organisations-search"
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setCurrentPage(DEFAULT_PAGE_NUMBER);
                }}
                placeholder={m.coInvestments_insights_searchPlaceholder()}
                value={searchQuery}
                withBorder
              />
              {renderColumnToggle}
            </div>
          ) : null}
          <DataGrid<LimitedPartnerInsight>
            columnDefs={columnDefs}
            defaultColDef={DEFAULT_COL_DEF}
            emptyState={{
              title: debouncedSearch
                ? m.common_noResultsFound()
                : m.coInvestments_insights_orgs_empty(),
              illustration: <ListIllustration />,
            }}
            getRowId={(params) => params.data.limitedPartnerId}
            loading={isPending}
            onRowClicked={handleRowClicked}
            onSortChanged={handleSortChanged}
            rowClass="cursor-pointer"
            rowData={limitedPartners}
            rowSelectionOptions={NO_SELECTION_OPTIONS}
            suppressMultiSort
          />
          {paginationElement}
        </>
      )}
    </div>
  );
};
